/**
 * Wallet storage — IndexedDB-backed persistence via `idb` library.
 *
 * Schema:
 * - Store "vault": single encrypted wallet vault (mnemonic + metadata)
 * - Store "transactions": tx history records (indexed by timestamp)
 *
 * Features:
 * - Versioned schema with migration support
 * - Quota exceeded detection
 * - Corrupt data recovery (offer re-import)
 * - Full clear/reset
 */

import { openDB, type IDBPDatabase } from 'idb';
import type { EncryptedVault, TxRecord } from './types.js';

const DB_NAME = 'cryptocall-wallet';
const DB_VERSION = 1;

interface WalletDB {
  vault: {
    key: string;
    value: {
      ciphertext: ArrayBuffer;
      iv: Uint8Array;
      salt: Uint8Array;
      version: number;
      createdAt: number;
      address: string;
    };
  };
  transactions: {
    key: string; // tx hash
    value: TxRecord;
    indexes: {
      'by-timestamp': number;
      'by-status': string;
    };
  };
}

let dbInstance: IDBPDatabase<WalletDB> | null = null;

async function getDb(): Promise<IDBPDatabase<WalletDB>> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<WalletDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Vault store
      if (!db.objectStoreNames.contains('vault')) {
        db.createObjectStore('vault');
      }

      // Transactions store
      if (!db.objectStoreNames.contains('transactions')) {
        const txStore = db.createObjectStore('transactions', { keyPath: 'hash' });
        txStore.createIndex('by-timestamp', 'timestamp');
        txStore.createIndex('by-status', 'status');
      }
    },
    blocked() {
      console.warn('[WalletStorage] Database upgrade blocked by another tab');
    },
    blocking() {
      dbInstance?.close();
      dbInstance = null;
    },
  });

  return dbInstance;
}

// ─── Vault operations ─────────────────────────────────────────────────────────

export async function hasVault(): Promise<boolean> {
  const db = await getDb();
  const vault = await db.get('vault', 'primary');
  return vault !== undefined;
}

export async function saveVault(
  vault: EncryptedVault,
  address: string
): Promise<void> {
  const db = await getDb();
  try {
    await db.put('vault', {
      ciphertext: vault.ciphertext,
      iv: vault.iv,
      salt: vault.salt,
      version: vault.version,
      createdAt: Date.now(),
      address,
    }, 'primary');
  } catch (err) {
    if (isQuotaExceeded(err)) {
      throw new WalletStorageError('QUOTA_EXCEEDED', 'Storage quota exceeded. Clear browser data.');
    }
    throw err;
  }
}

export async function loadVault(): Promise<{
  vault: EncryptedVault;
  address: string;
} | null> {
  const db = await getDb();
  const record = await db.get('vault', 'primary');
  if (!record) return null;

  return {
    vault: {
      ciphertext: record.ciphertext,
      iv: record.iv,
      salt: record.salt,
      version: record.version,
    },
    address: record.address,
  };
}

export async function deleteVault(): Promise<void> {
  const db = await getDb();
  await db.delete('vault', 'primary');
}

// ─── Transaction operations ───────────────────────────────────────────────────

export async function saveTx(tx: TxRecord): Promise<void> {
  const db = await getDb();
  try {
    await db.put('transactions', tx);
  } catch (err) {
    if (isQuotaExceeded(err)) {
      // On quota exceeded, delete oldest transactions to make room
      await pruneOldTransactions(50);
      await db.put('transactions', tx);
    } else {
      throw err;
    }
  }
}

export async function updateTxStatus(
  hash: string,
  status: TxRecord['status']
): Promise<void> {
  const db = await getDb();
  const tx = await db.get('transactions', hash);
  if (tx) {
    tx.status = status;
    await db.put('transactions', tx);
  }
}

export async function getTxHistory(
  limit: number = 50,
  offset: number = 0
): Promise<TxRecord[]> {
  const db = await getDb();
  const all = await db.getAllFromIndex('transactions', 'by-timestamp');
  // Reverse for newest-first
  all.reverse();
  return all.slice(offset, offset + limit);
}

export async function getTxByHash(hash: string): Promise<TxRecord | undefined> {
  const db = await getDb();
  return db.get('transactions', hash);
}

export async function getPendingTransactions(): Promise<TxRecord[]> {
  const db = await getDb();
  return db.getAllFromIndex('transactions', 'by-status', 'pending');
}

// ─── Utility ──────────────────────────────────────────────────────────────────

export async function clearAllData(): Promise<void> {
  const db = await getDb();
  await db.clear('vault');
  await db.clear('transactions');
}

async function pruneOldTransactions(keepCount: number): Promise<void> {
  const db = await getDb();
  const all = await db.getAllFromIndex('transactions', 'by-timestamp');
  if (all.length <= keepCount) return;

  const toDelete = all.slice(0, all.length - keepCount);
  const tx = db.transaction('transactions', 'readwrite');
  for (const record of toDelete) {
    await tx.store.delete(record.hash);
  }
  await tx.done;
}

function isQuotaExceeded(err: unknown): boolean {
  if (err instanceof DOMException) {
    return err.name === 'QuotaExceededError' || err.code === 22;
  }
  return false;
}

export class WalletStorageError extends Error {
  constructor(
    public readonly code: 'QUOTA_EXCEEDED' | 'NOT_FOUND' | 'CORRUPT',
    message: string
  ) {
    super(message);
    this.name = 'WalletStorageError';
  }
}
